<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import BarChart from 'shared/components/charts/BarChart.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import TeamTransferMatrix from './TeamTransferMatrix.vue';
import { formatTime } from '../constants';
import { useSummaryReportArchive } from '../composables/useSummaryReportArchive';
import {
  buildBucketArchiveFilters,
  buildDailyArchiveFilters,
  resolveResolutionBreakdownMetricKey,
} from '../helpers/summaryReportArchiveHelper';
import {
  formatDynamicsBucketLabel,
  formatPeriodRangeLabel,
  resolveDynamicsGranularity,
} from '../helpers/dynamicsGranularityHelper';
import { chartHasValues } from '../helpers/reportChartEmpty';

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
  archiveContext: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['chartsLoaded']);

const { t } = useI18n();
const loading = ref(false);
const chartData = ref({});
const comparisonChartData = ref([]);
const { openArchive, isMetricLinkable } = useSummaryReportArchive();

const scopeChartParams = () => ({
  type: props.type,
  businessHours: props.filters.businessHours,
  inboxIds: props.filters.inboxIds,
  teamIds: props.filters.teamIds,
  labelIds: props.filters.labelIds,
  excludeLabelIds: props.filters.excludeLabelIds,
  agentIds: props.filters.agentIds,
  channelTypes: props.filters.channelTypes,
  episodeResult: props.filters.episodeResult,
  receiptType: props.filters.receiptType,
  tagPresence: props.filters.tagPresence,
  resolutionMethod: props.filters.resolutionMethod,
  statusAtPeriodEnd: props.filters.statusAtPeriodEnd,
  reopening: props.filters.reopening,
  transfers: props.filters.transfers,
  humanResponse: props.filters.humanResponse,
});

let lastChartKey = '';

const fetchCharts = async () => {
  if (!props.filters.from) return;

  const chartKey = JSON.stringify({
    type: props.type,
    from: props.filters.from,
    to: props.filters.to,
    comparisonPeriods: props.filters.comparisonPeriods || [],
    inboxIds: props.filters.inboxIds,
    teamIds: props.filters.teamIds,
    labelIds: props.filters.labelIds,
    excludeLabelIds: props.filters.excludeLabelIds,
    agentIds: props.filters.agentIds,
    channelTypes: props.filters.channelTypes,
    episodeResult: props.filters.episodeResult,
    receiptType: props.filters.receiptType,
    tagPresence: props.filters.tagPresence,
    resolutionMethod: props.filters.resolutionMethod,
    statusAtPeriodEnd: props.filters.statusAtPeriodEnd,
    reopening: props.filters.reopening,
    transfers: props.filters.transfers,
    humanResponse: props.filters.humanResponse,
  });
  if (chartKey === lastChartKey) return;
  lastChartKey = chartKey;

  loading.value = true;
  try {
    const baseParams = scopeChartParams();
    const periods = props.filters.comparisonPeriods || [];
    const [primary, ...comparisonResponses] = await Promise.all([
      ReportsAPI.getSummaryCharts({
        ...baseParams,
        from: props.filters.from,
        to: props.filters.to,
      }),
      ...periods.map(period =>
        ReportsAPI.getSummaryCharts({
          ...baseParams,
          from: period.from,
          to: period.to,
        })
      ),
    ]);
    chartData.value = primary.data || {};
    comparisonChartData.value = comparisonResponses.map((response, index) => ({
      from: periods[index]?.from,
      to: periods[index]?.to,
      data: response.data || {},
    }));
    emit('chartsLoaded', chartData.value);
  } catch {
    chartData.value = {};
    comparisonChartData.value = [];
    emit('chartsLoaded', {});
    lastChartKey = '';
  } finally {
    loading.value = false;
  }
};

watch(() => props.filters, fetchCharts, { deep: true, immediate: true });

const dynamicsGranularity = computed(
  () =>
    chartData.value.dynamics_group_by ||
    resolveDynamicsGranularity(props.filters.from, props.filters.to)
);

const incompleteText = computed(() => t('REPORT.COMPARISON.INCOMPLETE_DAY'));

const formatDynamicsLabels = rows =>
  rows.map(row =>
    formatDynamicsBucketLabel(
      row.period_bucket_start_utc || row.date,
      dynamicsGranularity.value,
      incompleteText.value
    )
  );

const COMPARISON_LINE_STYLES = [
  { borderColor: '#93C5FD', backgroundColor: '#93C5FD' },
  { borderColor: '#6EE7B7', backgroundColor: '#6EE7B7' },
  { borderColor: '#FCD34D', backgroundColor: '#FCD34D' },
];

const appendComparisonDynamicsDatasets = (primaryDatasets, valueKeys) => {
  if (!comparisonChartData.value.length) return primaryDatasets;

  const primaryRows = chartData.value.daily_dynamics || [];
  const comparisonDatasets = [];

  comparisonChartData.value.forEach((slice, sliceIndex) => {
    const rows = slice.data?.daily_dynamics || [];
    const style =
      COMPARISON_LINE_STYLES[sliceIndex % COMPARISON_LINE_STYLES.length];
    const periodLabel =
      formatPeriodRangeLabel(slice.from, slice.to) ||
      t('REPORT.COMPARISON.LEGEND_EXTRA', { n: sliceIndex + 1 });

    primaryDatasets.forEach((dataset, datasetIndex) => {
      const sourceKey = valueKeys[datasetIndex];
      comparisonDatasets.push({
        label: `${dataset.label} (${periodLabel})`,
        data: primaryRows.map((_, index) => {
          const point = rows[index];
          if (!point) return 0;
          return point[sourceKey] ?? point.created ?? 0;
        }),
        borderColor: style.borderColor,
        backgroundColor: style.backgroundColor,
        borderDash: [6, 4],
        tension: 0.3,
      });
    });
  });

  return [...primaryDatasets, ...comparisonDatasets];
};

const rankingChartHeight = rowCount =>
  Math.max(288, Math.min((rowCount || 1) * 32, 560));

const topResolvedCollection = computed(() => {
  const rows = chartData.value.top_resolved || [];
  return {
    labels: rows.map(row => row.name),
    datasets: [
      {
        label: 'Resolved',
        data: rows.map(row => row.value),
        backgroundColor: '#3B82F6',
      },
    ],
  };
});

const topFirstResponseCollection = computed(() => {
  const rows = chartData.value.top_first_response || [];
  return {
    labels: rows.map(row => row.name),
    datasets: [
      {
        label: 'Median FRT',
        data: rows.map(row => row.value),
        backgroundColor: '#F59E0B',
      },
    ],
  };
});

const topTagsCollection = computed(() => {
  const rows = chartData.value.top_tags || [];
  return {
    labels: rows.map(row => row.name),
    datasets: [
      {
        label: 'Appeals',
        data: rows.map(row => row.value),
        backgroundColor: '#6366F1',
      },
    ],
  };
});

const inboxVolumeCollection = computed(() => {
  const rows = chartData.value.volume || [];
  return {
    labels: rows.map(row => row.name),
    datasets: [
      {
        label: 'Created',
        data: rows.map(row => row.created),
        backgroundColor: '#3B82F6',
      },
      {
        label: 'Resolved',
        data: rows.map(row => row.resolved),
        backgroundColor: '#10B981',
      },
    ],
  };
});

const teamThroughputCollection = computed(() => {
  const rows = chartData.value.team_throughput || [];
  return {
    labels: rows.map(row => row.name),
    datasets: [
      {
        label: 'Received',
        data: rows.map(row => row.received),
        backgroundColor: '#3B82F6',
      },
      {
        label: 'Completed',
        data: rows.map(row => row.completed),
        backgroundColor: '#10B981',
      },
    ],
  };
});

const teamFirstResponseCollection = computed(() => {
  const rows = chartData.value.top_first_response || [];
  return {
    labels: rows.map(row => row.name),
    datasets: [
      {
        label: 'Median FRT',
        data: rows.map(row => row.value),
        backgroundColor: '#F59E0B',
      },
    ],
  };
});

const teamDynamicsCollection = computed(() => {
  const rows = chartData.value.daily_dynamics || [];
  const datasets = appendComparisonDynamicsDatasets(
    [
      {
        label: 'Started',
        metricKey: 'assignmentEpisodesCount',
        data: rows.map(row => row.started ?? row.created),
        borderColor: '#3B82F6',
        backgroundColor: '#3B82F6',
        tension: 0.3,
      },
      {
        label: 'Resolved',
        metricKey: 'resolutionsCount',
        data: rows.map(row => row.resolved),
        borderColor: '#10B981',
        backgroundColor: '#10B981',
        tension: 0.3,
      },
      {
        label: 'Transferred',
        metricKey: 'transfersOutCount',
        data: rows.map(row => row.transferred || 0),
        borderColor: '#F59E0B',
        backgroundColor: '#F59E0B',
        tension: 0.3,
      },
    ],
    ['started', 'resolved', 'transferred']
  );

  return {
    labels: formatDynamicsLabels(rows),
    datasets,
  };
});

const agentDynamicsCollection = computed(() => {
  const rows = chartData.value.daily_dynamics || [];
  const datasets = appendComparisonDynamicsDatasets(
    [
      {
        label: t('SUMMARY_REPORTS.CHARTS.SERIES.ACCEPTED'),
        metricKey: 'assignmentEpisodesCount',
        data: rows.map(row => row.created),
        borderColor: '#3B82F6',
        backgroundColor: '#3B82F6',
        tension: 0.3,
      },
      {
        label: t('SUMMARY_REPORTS.CHARTS.SERIES.RESOLVED'),
        metricKey: 'resolutionsCount',
        data: rows.map(row => row.resolved),
        borderColor: '#10B981',
        backgroundColor: '#10B981',
        tension: 0.3,
      },
      {
        label: t('SUMMARY_REPORTS.CHARTS.SERIES.TRANSFERRED'),
        data: rows.map(row => row.transferred || 0),
        borderColor: '#F59E0B',
        backgroundColor: '#F59E0B',
        tension: 0.3,
      },
    ],
    ['created', 'resolved', 'transferred']
  );

  return {
    labels: formatDynamicsLabels(rows),
    datasets,
  };
});

const accountDynamicsCollection = computed(() => {
  const rows = chartData.value.daily_dynamics || [];
  const datasets = appendComparisonDynamicsDatasets(
    [
      {
        label: t('SUMMARY_REPORTS.CHARTS.SERIES.CREATED'),
        metricKey: 'conversationsCount',
        data: rows.map(row => row.created),
        borderColor: '#3B82F6',
        backgroundColor: '#3B82F6',
        tension: 0.3,
      },
      {
        label: t('SUMMARY_REPORTS.CHARTS.SERIES.RESOLVED'),
        metricKey: 'resolutionsCount',
        data: rows.map(row => row.resolved),
        borderColor: '#10B981',
        backgroundColor: '#10B981',
        tension: 0.3,
      },
    ],
    ['created', 'resolved']
  );

  return {
    labels: formatDynamicsLabels(rows),
    datasets,
  };
});

const accountResolvedByCollection = computed(() => {
  const rows = chartData.value.daily_dynamics || [];
  return {
    labels: formatDynamicsLabels(rows),
    datasets: [
      {
        label: t('SUMMARY_REPORTS.CHARTS.SERIES.BOT_RESOLVED'),
        data: rows.map(row => row.bot_resolved || 0),
        backgroundColor: '#3B82F6',
        stack: 'resolved-by',
      },
      {
        label: t('SUMMARY_REPORTS.CHARTS.SERIES.HUMAN_RESOLVED'),
        data: rows.map(row => row.human_resolved || 0),
        backgroundColor: '#10B981',
        stack: 'resolved-by',
      },
      {
        label: t('SUMMARY_REPORTS.CHARTS.SERIES.UNRESOLVED'),
        data: rows.map(row => row.unresolved || 0),
        backgroundColor: '#F59E0B',
        stack: 'resolved-by',
      },
    ],
  };
});

const stackedBarChartOptions = {
  scales: {
    x: {
      stacked: true,
      ticks: { fontFamily: 'Inter,-apple-system,system-ui,sans-serif' },
      grid: { drawOnChartArea: false },
    },
    y: {
      stacked: true,
      beginAtZero: true,
      ticks: {
        fontFamily: 'Inter,-apple-system,system-ui,sans-serif',
        stepSize: 1,
      },
      grid: { drawOnChartArea: false },
    },
  },
};

const DYNAMICS_LINE_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#6366F1',
  '#EF4444',
];

const buildSelectedDynamicsCollection = entities => {
  const dates = [
    ...new Set(
      entities.flatMap(entity => (entity.series || []).map(point => point.date))
    ),
  ].sort();

  return {
    labels: dates.map(date =>
      formatDynamicsBucketLabel(
        date,
        dynamicsGranularity.value,
        incompleteText.value
      )
    ),
    datasets: entities.map((entity, index) => ({
      label: entity.name,
      data: dates.map(date => {
        const point = (entity.series || []).find(row => row.date === date);
        return point?.created || 0;
      }),
      borderColor: DYNAMICS_LINE_COLORS[index % DYNAMICS_LINE_COLORS.length],
      backgroundColor:
        DYNAMICS_LINE_COLORS[index % DYNAMICS_LINE_COLORS.length],
      tension: 0.3,
    })),
  };
};

const selectedLabelDynamicsCollection = computed(() => {
  const entities = chartData.value.selected_dynamics || [];
  if (!entities.length) return null;
  return buildSelectedDynamicsCollection(entities);
});

const selectedInboxDynamicsCollection = computed(() => {
  const entities = chartData.value.selected_dynamics || [];
  if (!entities.length) return null;
  return buildSelectedDynamicsCollection(entities);
});

const medianFrtChartOptions = {
  scales: {
    x: {
      ticks: {
        callback: value => formatTime(value),
      },
    },
  },
  plugins: {
    tooltip: {
      callbacks: {
        label: context => formatTime(context.parsed.x),
      },
    },
  },
};

const showTopResolved = computed(
  () => props.type === 'agent' && chartHasValues(topResolvedCollection.value)
);
const showTopFirstResponse = computed(
  () =>
    props.type === 'agent' && chartHasValues(topFirstResponseCollection.value)
);
const showTopTags = computed(
  () => props.type === 'label' && (chartData.value.top_tags || []).length
);
const showInboxVolume = computed(
  () => props.type === 'inbox' && (chartData.value.volume || []).length
);
const showTeamDynamics = computed(
  () => props.type === 'team' && chartHasValues(teamDynamicsCollection.value)
);
const showTeamThroughput = computed(
  () => props.type === 'team' && (chartData.value.team_throughput || []).length
);
const showTeamFirstResponse = computed(
  () =>
    props.type === 'team' && (chartData.value.top_first_response || []).length
);
const showAgentDynamics = computed(
  () => props.type === 'agent' && chartHasValues(agentDynamicsCollection.value)
);
const showAccountDynamics = computed(
  () =>
    props.type === 'account' && chartHasValues(accountDynamicsCollection.value)
);
const showAccountResolvedBy = computed(
  () =>
    props.type === 'account' &&
    chartHasValues(accountResolvedByCollection.value)
);
const showChatsResolvedBy = computed(
  () =>
    props.type === 'chats' && chartHasValues(accountResolvedByCollection.value)
);
const chatsFrtBucketsCollection = computed(() => {
  const rows = chartData.value.frt_buckets || [];
  return {
    labels: rows.map(
      row => t(`CHATS_REPORTS.BUCKETS.FRT.${row.key}`) || row.label || row.key
    ),
    datasets: [
      {
        label: t('CHATS_REPORTS.CHARTS.FIRST_RESPONSE'),
        data: rows.map(row => row.value || 0),
        backgroundColor: '#3B82F6',
      },
    ],
  };
});

const chatsResolutionBucketsCollection = computed(() => {
  const rows = chartData.value.resolution_buckets || [];
  return {
    labels: rows.map(
      row =>
        t(`CHATS_REPORTS.BUCKETS.RESOLUTION.${row.key}`) || row.label || row.key
    ),
    datasets: [
      {
        label: t('CHATS_REPORTS.CHARTS.RESOLUTION_TIME'),
        data: rows.map(row => row.value || 0),
        backgroundColor: '#10B981',
      },
    ],
  };
});

const showChatsFrtBuckets = computed(
  () =>
    props.type === 'chats' && chartHasValues(chatsFrtBucketsCollection.value)
);
const showChatsResolutionBuckets = computed(
  () =>
    props.type === 'chats' &&
    chartHasValues(chatsResolutionBucketsCollection.value)
);
const showSelectedLabelDynamics = computed(
  () => props.type === 'label' && selectedLabelDynamicsCollection.value
);
const showSelectedInboxDynamics = computed(
  () => props.type === 'inbox' && selectedInboxDynamicsCollection.value
);
const showTransferMatrix = computed(
  () => props.type === 'team' && chartData.value.transfer_matrix
);

const topResolvedHeight = computed(() =>
  rankingChartHeight(chartData.value.top_resolved?.length)
);
const topFirstResponseHeight = computed(() =>
  rankingChartHeight(chartData.value.top_first_response?.length)
);
const topTagsHeight = computed(() =>
  rankingChartHeight(chartData.value.top_tags?.length)
);
const teamThroughputHeight = computed(() =>
  rankingChartHeight(chartData.value.team_throughput?.length)
);
const teamFirstResponseHeight = computed(() =>
  rankingChartHeight(chartData.value.top_first_response?.length)
);

const chartRowsByType = computed(() => ({
  top_resolved: chartData.value.top_resolved || [],
  top_tags: chartData.value.top_tags || [],
  volume: chartData.value.volume || [],
  team_throughput: chartData.value.team_throughput || [],
}));

const onBarChartClick =
  ({ chartKey, metricKey }) =>
  event => {
    if (!isMetricLinkable(metricKey, props.type)) return;

    const rows = chartRowsByType.value[chartKey] || [];
    const row = rows[event.index];
    if (!row?.id) return;

    openArchive({
      metricKey,
      reportType: props.type,
      row: { id: row.id, name: row.name },
      context: props.archiveContext,
    });
  };

const onVolumeBarClick = event => {
  const rows = chartData.value.volume || [];
  const row = rows[event.index];
  if (!row?.id) return;

  const metricKey =
    event.datasetIndex === 1 ? 'resolutionsCount' : 'conversationsCount';
  if (!isMetricLinkable(metricKey, props.type)) return;

  openArchive({
    metricKey,
    reportType: props.type,
    row: { id: row.id, name: row.name },
    context: props.archiveContext,
  });
};

const onTeamThroughputClick = event => {
  const rows = chartData.value.team_throughput || [];
  const row = rows[event.index];
  if (!row?.id) return;

  const metricKey =
    event.datasetIndex === 1 ? 'resolutionsCount' : 'assignmentEpisodesCount';
  if (!isMetricLinkable(metricKey, props.type)) return;

  openArchive({
    metricKey,
    reportType: props.type,
    row: { id: row.id, name: row.name },
    context: props.archiveContext,
  });
};

const onOverviewDynamicsPointClick = (collection, event) => {
  const rows = chartData.value.daily_dynamics || [];
  const dateLabel = rows[event.index]?.date;
  if (!dateLabel) return;

  // Comparison-period lines carry no metricKey, so clicks on them do nothing
  const { metricKey } = collection.datasets[event.datasetIndex] || {};
  if (!metricKey) return;

  const filters =
    props.type === 'account'
      ? buildBucketArchiveFilters({
          metricKey,
          reportType: props.type,
          row: null,
          context: props.archiveContext,
          dateLabel,
          granularity: dynamicsGranularity.value,
        })
      : buildDailyArchiveFilters({
          metricKey,
          reportType: props.type,
          row: null,
          context: props.archiveContext,
          dateLabel,
        });

  openArchive({
    metricKey,
    reportType: props.type,
    row: null,
    context: props.archiveContext,
    filters,
  });
};

const onAccountResolvedByClick = event => {
  const rows = chartData.value.daily_dynamics || [];
  const dateLabel = rows[event.index]?.date;
  if (!dateLabel) return;

  const metricKey = resolveResolutionBreakdownMetricKey(event.datasetIndex);
  const reportType = props.type === 'chats' ? 'account' : props.type;
  if (!isMetricLinkable(metricKey, reportType)) return;

  const filters = buildBucketArchiveFilters({
    metricKey,
    reportType,
    row: null,
    context: props.archiveContext,
    dateLabel,
    granularity: dynamicsGranularity.value,
  });

  openArchive({
    metricKey,
    reportType,
    row: null,
    context: props.archiveContext,
    filters,
  });
};

const onChatsBucketClick = metricKey => event => {
  const rows =
    metricKey === 'firstResponseCount'
      ? chartData.value.frt_buckets || []
      : chartData.value.resolution_buckets || [];
  const row = rows[event.index];
  if (!row?.value) return;

  openArchive({
    metricKey,
    reportType: 'account',
    row: null,
    context: props.archiveContext,
  });
};

const onSelectedDynamicsPointClick = event => {
  const entities = chartData.value.selected_dynamics || [];
  const entity = entities[event.datasetIndex];
  if (!entity?.id) return;

  const dates = [
    ...new Set(
      entities.flatMap(item => (item.series || []).map(point => point.date))
    ),
  ].sort();
  const dateLabel = dates[event.index];
  if (!dateLabel) return;

  const filters = buildDailyArchiveFilters({
    metricKey: 'conversationsCount',
    reportType: props.type,
    row: { id: entity.id, name: entity.name },
    context: props.archiveContext,
    dateLabel,
  });

  openArchive({
    metricKey: 'conversationsCount',
    reportType: props.type,
    row: { id: entity.id, name: entity.name },
    context: props.archiveContext,
    filters,
  });
};
</script>

<template>
  <div v-if="loading" class="mt-4 text-sm text-n-slate-11">
    {{ $t('SUMMARY_REPORTS.CHARTS.LOADING') }}
  </div>
  <div v-else class="mt-4 flex flex-col gap-4">
    <section
      v-if="showTopResolved"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.TOP_RESOLVED') }}
      </h3>
      <div class="mt-4" :style="{ height: `${topResolvedHeight}px` }">
        <BarChart
          :collection="topResolvedCollection"
          horizontal
          :show-legend="false"
          @bar-click="
            onBarChartClick({
              chartKey: 'top_resolved',
              metricKey: 'resolutionsCount',
            })
          "
        />
      </div>
    </section>

    <section
      v-if="showTopFirstResponse"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.TOP_MEDIAN_FRT') }}
      </h3>
      <div class="mt-4" :style="{ height: `${topFirstResponseHeight}px` }">
        <BarChart
          :collection="topFirstResponseCollection"
          horizontal
          :show-legend="false"
          :chart-options="medianFrtChartOptions"
        />
      </div>
    </section>

    <section
      v-if="showTopTags"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.TOP_TAGS') }}
      </h3>
      <div class="mt-4" :style="{ height: `${topTagsHeight}px` }">
        <BarChart
          :collection="topTagsCollection"
          horizontal
          :show-legend="false"
          @bar-click="
            onBarChartClick({
              chartKey: 'top_tags',
              metricKey: 'conversationsCount',
            })
          "
        />
      </div>
    </section>

    <section
      v-if="showAgentDynamics"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.AGENT_DYNAMICS') }}
      </h3>
      <div class="mt-4 h-72">
        <LineChart
          :collection="agentDynamicsCollection"
          @point-click="
            onOverviewDynamicsPointClick(agentDynamicsCollection, $event)
          "
        />
      </div>
    </section>

    <section
      v-if="showAccountDynamics"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.ACCOUNT_DYNAMICS') }}
      </h3>
      <div class="mt-4 h-72">
        <LineChart
          :collection="accountDynamicsCollection"
          @point-click="
            onOverviewDynamicsPointClick(accountDynamicsCollection, $event)
          "
        />
      </div>
    </section>

    <section
      v-if="showAccountResolvedBy"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.RESOLVED_BY_WHOM') }}
      </h3>
      <div class="mt-4 h-72">
        <BarChart
          :collection="accountResolvedByCollection"
          show-legend
          :chart-options="stackedBarChartOptions"
          @bar-click="onAccountResolvedByClick"
        />
      </div>
    </section>

    <section
      v-if="showChatsFrtBuckets"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CHATS_REPORTS.CHARTS.FIRST_RESPONSE') }}
      </h3>
      <div class="mt-4 h-72">
        <BarChart
          :collection="chatsFrtBucketsCollection"
          :show-legend="false"
          @bar-click="onChatsBucketClick('firstResponseCount')"
        />
      </div>
    </section>

    <section
      v-if="showChatsResolutionBuckets"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CHATS_REPORTS.CHARTS.RESOLUTION_TIME') }}
      </h3>
      <div class="mt-4 h-72">
        <BarChart
          :collection="chatsResolutionBucketsCollection"
          :show-legend="false"
          @bar-click="onChatsBucketClick('resolutionsCount')"
        />
      </div>
    </section>

    <section
      v-if="showChatsResolvedBy"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CHATS_REPORTS.CHARTS.CYCLE_RESULT') }}
      </h3>
      <div class="mt-4 h-72">
        <BarChart
          :collection="accountResolvedByCollection"
          show-legend
          :chart-options="stackedBarChartOptions"
          @bar-click="onAccountResolvedByClick"
        />
      </div>
    </section>

    <section
      v-if="showSelectedLabelDynamics"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.SELECTED_LABEL_DYNAMICS') }}
      </h3>
      <div class="mt-4 h-72">
        <LineChart
          :collection="selectedLabelDynamicsCollection"
          @point-click="onSelectedDynamicsPointClick"
        />
      </div>
    </section>

    <section
      v-if="showTeamThroughput"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.TEAM_THROUGHPUT') }}
      </h3>
      <div class="mt-4" :style="{ height: `${teamThroughputHeight}px` }">
        <BarChart
          :collection="teamThroughputCollection"
          horizontal
          show-legend
          @bar-click="onTeamThroughputClick"
        />
      </div>
    </section>

    <section
      v-if="showTeamFirstResponse"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.TEAM_MEDIAN_FRT') }}
      </h3>
      <div class="mt-4" :style="{ height: `${teamFirstResponseHeight}px` }">
        <BarChart
          :collection="teamFirstResponseCollection"
          horizontal
          :show-legend="false"
          :chart-options="medianFrtChartOptions"
        />
      </div>
    </section>

    <section
      v-if="showTeamDynamics"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.TEAM_DYNAMICS') }}
      </h3>
      <div class="mt-4 h-72">
        <LineChart
          :collection="teamDynamicsCollection"
          @point-click="
            onOverviewDynamicsPointClick(teamDynamicsCollection, $event)
          "
        />
      </div>
    </section>

    <section
      v-if="showInboxVolume"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.INBOX_VOLUME') }}
      </h3>
      <div class="mt-4 h-72">
        <BarChart
          :collection="inboxVolumeCollection"
          show-legend
          @bar-click="onVolumeBarClick"
        />
      </div>
    </section>

    <section
      v-if="showSelectedInboxDynamics"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.SELECTED_INBOX_DYNAMICS') }}
      </h3>
      <div class="mt-4 h-72">
        <LineChart
          :collection="selectedInboxDynamicsCollection"
          @point-click="onSelectedDynamicsPointClick"
        />
      </div>
    </section>

    <TeamTransferMatrix
      v-if="showTransferMatrix"
      :matrix-data="chartData.transfer_matrix"
      :archive-context="archiveContext"
    />
  </div>
</template>
